import { FC, useMemo } from "react";
import { Switch, Route as ReactRoute, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { Route } from "App/router";
import { useScrollToTopOnRouteChange, buildUrl } from "shared/utils";
import { FourOhFour, StatusPage } from "shared/pages";
import {
  BandProvider,
  BandSubscriptionProvider,
  CalendarProvider,
  CompanyProvider,
  PermissionsProvider,
  useBandContext,
  usePermissionsContext,
} from "Bands/contexts";
import { bandRoutes } from "Bands/router";
import { AUTH_ACTIONS, IUser } from "Auth/types";
import { Layout } from "shared/layout";
import { authRoutes } from "Auth/router";
import { authMetaSelector } from "Auth/state";
import { IRouteType, IStoreState } from "App/types";
import { IAPIState } from "shared/types";
import { Loader } from "shared/components";
import { SubscriptionMessage } from "Bands/containers";
import { BAND_PATHS } from "./constants";
import { dataMetaSelector } from "Bands/state";
import { API_ENDPOINTS } from "shared/constants";
import { useVenueEventContext, VenueEventProvider } from "Venues/contexts";
import { ProfileProvider } from "Auth/contexts";

interface BandsRouterProps {
  currentUser: IUser | null;
  handleOpenCookies: () => void;
}

export const BandsRouterComponent: FC<BandsRouterProps> = ({ currentUser, handleOpenCookies }) => {
  const userMeta = useSelector((state: IStoreState) =>
    authMetaSelector(state, AUTH_ACTIONS.getCurrentUser)
  );
  const internalUserMeta = useSelector((state: IStoreState) =>
    authMetaSelector(state, AUTH_ACTIONS.getCurrentInternalUser)
  );
  const { bandPermissionsArray } = usePermissionsContext();

  const { band, bandId } = useBandContext();
  const { pathname } = useLocation();
  const isSubscriptionPath = pathname.startsWith(
    buildUrl(BAND_PATHS.SUBSCRIPTION, { bandId: bandId || "" })
  );
  const isExportPath = pathname.startsWith(buildUrl(BAND_PATHS._EVENT_DETAILS_EXPORT, { id: "" }));

  const { api: fetchBandApi } = useSelector(dataMetaSelector(API_ENDPOINTS.BAND(bandId || "")));

  useScrollToTopOnRouteChange();

  const { event } = useVenueEventContext();

  const renderContent = useMemo(() => {
    if (userMeta?.status !== IAPIState.failed && internalUserMeta?.status === IAPIState.pending)
      return <Loader />;
    if (
      internalUserMeta?.status === IAPIState.failed ||
      fetchBandApi?.status === IAPIState.failed
    ) {
      <StatusPage status={internalUserMeta?.error?.status || fetchBandApi?.error?.status} />;
    }

    return (
      <Switch>
        {bandRoutes.map((route, index) => (
          <Route
            key={index}
            {...route}
            permissions={route.type === IRouteType.permission ? bandPermissionsArray : undefined}
          />
        ))}
        {authRoutes.map((route, index) => (
          <Route key={index} {...route} />
        ))}
        <ReactRoute component={FourOhFour} />
      </Switch>
    );
  }, [
    bandPermissionsArray,
    fetchBandApi?.error?.status,
    fetchBandApi?.status,
    internalUserMeta?.error?.status,
    internalUserMeta?.status,
    userMeta?.status,
  ]);

  if (isExportPath) return renderContent;

  return (
    <>
      {bandId && band && !isSubscriptionPath && <SubscriptionMessage />}
      <Layout
        event={event}
        currentUser={currentUser || undefined}
        handleOpenCookies={handleOpenCookies}
      >
        {renderContent}
      </Layout>
    </>
  );
};

export const BandsRouter: FC<BandsRouterProps> = props => (
  <ProfileProvider>
    <CompanyProvider>
      <BandProvider>
        <PermissionsProvider>
          <BandSubscriptionProvider>
            <CalendarProvider>
              <VenueEventProvider>
                <BandsRouterComponent {...props} />
              </VenueEventProvider>
            </CalendarProvider>
          </BandSubscriptionProvider>
        </PermissionsProvider>
      </BandProvider>
    </CompanyProvider>
  </ProfileProvider>
);
